import { Routes, UrlSegment } from '@angular/router';
import { authGuard, rolesGuard, auctioneerProfileGuard } from './guards/auth.guard';

/** Known route paths - public and protected */
const PUBLIC_PATHS = ['login', 'unauthorized', 'auth'];
const PROTECTED_PATHS = [
  'dashboard', 'auctions', 'commodities', 'members', 'farmers', 'traders',
  'auctioneers', 'requests', 'taxes', 'tax-components', 'tax-settings',
  'settings', 'tds-config', 'tally-settings', 'sequence-generator', 'users', 'invoices',
  'seller-statements', 'commission-bills', 'service-bills', 'trader-invoices',
  'registrations', 'activities', 'market-analysis'
];

/** URL matcher for 404 - matches paths that are NOT known routes */
export function notFoundMatcher(segments: UrlSegment[]) {
  if (segments.length === 0) {
    return null; // Empty path goes to protected routes (dashboard redirect)
  }

  const firstSegment = segments[0].path;
  const isKnownPath = [...PUBLIC_PATHS, ...PROTECTED_PATHS].includes(firstSegment);

  if (isKnownPath) {
    return null; // Known path - let other routes handle it
  }

  // Unknown path - show 404 page
  return { consumed: segments };
}

export const routes: Routes = [
  // 404 Not Found - MUST BE FIRST to catch unknown paths before auth redirect
  {
    matcher: notFoundMatcher,
    loadComponent: () => import('./auth/not-found/not-found.component').then(m => m.NotFoundComponent)
  },
  // Public login route
  {
    path: 'login',
    loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent)
  },
  // Unauthorized page (public route)
  {
    path: 'unauthorized',
    loadComponent: () => import('./auth/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent)
  },
  // Auth routes (no layout)
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
      }
    ]
  },
  // Protected routes with layout
  {
    path: '',
    loadComponent: () => import('./layout/main-layout.component').then(m => m.MainLayoutComponent),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./home/home.component').then(m => m.HomeComponent)
      },
      {
        path: 'auctions',
        redirectTo: 'auctions/online',
        pathMatch: 'full'
      },
      {
        path: 'auctions/online',
        loadComponent: () => import('./features/auctions/auction-list.component').then(m => m.AuctionListComponent),
        data: { auctionType: 'Online' }
      },
      {
        path: 'auctions/offline',
        loadComponent: () => import('./features/auctions/auction-list.component').then(m => m.AuctionListComponent),
        data: { auctionType: 'Offline' }
      },
      {
        path: 'auctions/add',
        loadComponent: () => import('./features/auctions/auction-form.component').then(m => m.AuctionFormComponent)
      },
      {
        path: 'auctions/view/:id',
        loadComponent: () => import('./features/auctions/auction-view.component').then(m => m.AuctionViewComponent)
      },
      {
        path: 'auctions/:id',
        loadComponent: () => import('./features/auctions/auction-form.component').then(m => m.AuctionFormComponent)
      },
      {
        path: 'auctions/:id/edit',
        loadComponent: () => import('./features/auctions/auction-form.component').then(m => m.AuctionFormComponent)
      },
      {
        path: 'auctions/:id/lots',
        loadComponent: () => import('./features/lots/lot-management.component').then(m => m.LotManagementComponent)
      },
      {
        path: 'auctions/:id/lots/:view',
        loadComponent: () => import('./features/lots/lot-management.component').then(m => m.LotManagementComponent)
      },
      {
        path: 'auctions/:id/lots/:view/:mode',
        loadComponent: () => import('./features/lots/lot-management.component').then(m => m.LotManagementComponent)
      },
      {
        path: 'auctions/:id/live-dashboard',
        loadComponent: () => import('./features/auctions/live-auction-dashboard.component').then(m => m.LiveAuctionDashboardComponent)
      },{
        path: 'auctions/:id/invoices',
        loadComponent: () => import('./features/auctions/auction-invoices.component').then(m => m.AuctionInvoicesComponent)
      },
      {
        path: 'auctions/:id/invoices/:filter',
        loadComponent: () => import('./features/auctions/auction-invoices.component').then(m => m.AuctionInvoicesComponent)
      },
      {
        path: 'commodities',
        loadComponent: () => import('./features/commodities/commodities-list.component').then(m => m.CommoditiesListComponent)
      },
      {
        path: 'commodities/add',
        loadComponent: () => import('./features/commodities/commodity-form.component').then(m => m.CommodityFormComponent)
      },
      {
        path: 'commodities/:id',
        loadComponent: () => import('./features/commodities/commodity-view.component').then(m => m.CommodityViewComponent)
      },
      {
        path: 'commodities/:id/edit',
        loadComponent: () => import('./features/commodities/commodity-form.component').then(m => m.CommodityFormComponent)
      },
      {
        path: 'members',
        loadComponent: () => import('./features/members/members-list.component').then(m => m.MembersListComponent),
        data: { memberType: 'all' }
      },
      {
        path: 'farmers',
        loadComponent: () => import('./features/members/members-list.component').then(m => m.MembersListComponent),
        data: { memberType: 'farmer' }
      },
      {
        path: 'traders',
        loadComponent: () => import('./features/members/members-list.component').then(m => m.MembersListComponent),
        data: { memberType: 'trader' }
      },
      {
        path: 'members/add',
        loadComponent: () => import('./features/members/member-form.component').then(m => m.MemberFormComponent)
      },
      {
        path: 'farmers/add',
        loadComponent: () => import('./features/members/member-form.component').then(m => m.MemberFormComponent),
        data: { memberType: 'farmer' }
      },
      {
        path: 'traders/add',
        loadComponent: () => import('./features/members/member-form.component').then(m => m.MemberFormComponent),
        data: { memberType: 'trader' }
      },
      {
        path: 'members/:id/edit',
        loadComponent: () => import('./features/members/member-form.component').then(m => m.MemberFormComponent)
      },
      {
        path: 'farmers/:id/edit',
        loadComponent: () => import('./features/members/member-form.component').then(m => m.MemberFormComponent),
        data: { memberType: 'farmer' }
      },
      {
        path: 'traders/:id/edit',
        loadComponent: () => import('./features/members/member-form.component').then(m => m.MemberFormComponent),
        data: { memberType: 'trader' }
      },
      {
        path: 'auctioneers',
        loadComponent: () => import('./features/auctioneers/auctioneers-list.component').then(m => m.AuctioneersListComponent),
        canActivate: [rolesGuard(['admin', 'super_admin'])]
      },
      {
        path: 'auctioneers/add',
        loadComponent: () => import('./features/auctioneers/auctioneer-form.component').then(m => m.AuctioneerFormComponent),
        canActivate: [rolesGuard(['admin', 'super_admin'])]
      },
      {
        path: 'auctioneers/edit/:id',
        loadComponent: () => import('./features/auctioneers/auctioneer-form.component').then(m => m.AuctioneerFormComponent),
        canActivate: [auctioneerProfileGuard]
      },
      {
        path: 'auctioneers/view/:id',
        loadComponent: () => import('./features/auctioneers/auctioneer-form.component').then(m => m.AuctioneerFormComponent),
        canActivate: [auctioneerProfileGuard]
      },
      {
        path: 'requests',
        loadComponent: () => import('./features/registrations/registration-list.component').then(m => m.RegistrationListComponent),
        data: { requestType: 'auctioneer-access' }
      },
      {
        path: 'taxes',
        loadComponent: () => import('./features/taxes/taxes').then(m => m.TaxesComponent),
        children: [
          {
            path: '',
            redirectTo: 'sets',
            pathMatch: 'full'
          },
          {
            path: 'sets',
            loadComponent: () => import('./features/tax-settings/tax-settings').then(m => m.TaxSettingsComponent)
          },
          {
            path: 'components',
            loadComponent: () => import('./features/tax-components/tax-components').then(m => m.TaxComponentsComponent)
          }
        ]
      },
      {
        path: 'tax-components',
        loadComponent: () => import('./features/tax-components/tax-components').then(m => m.TaxComponentsComponent)
      },
      {
        path: 'tax-settings',
        loadComponent: () => import('./features/tax-settings/tax-settings').then(m => m.TaxSettingsComponent)
      },
      {
        path: 'settings',
        loadComponent: () => import('./features/settings/auction-settings.component').then(m => m.AuctionSettingsComponent)
      },
      {
        path: 'tds-config',
        loadComponent: () => import('./features/tds-config/components/tds-config.component').then(m => m.TdsConfigComponent)
      },
      {
        path: 'tally-settings',
        loadComponent: () => import('./features/settings/tally-settings/tally-settings-list.component').then(m => m.TallySettingsListComponent)
      },
      {
        path: 'tally-settings/:auctioneerId',
        loadComponent: () => import('./features/settings/tally-settings/tally-settings.component').then(m => m.TallySettingsComponent)
      },
      {
        path: 'sequence-generator',
        loadComponent: () => import('./features/sequence-generator/components/sequence-generator.component').then(m => m.SequenceGeneratorComponent)
      },
      {
        path: 'users',
        loadComponent: () => import('./features/users/user-management.component').then(m => m.UserManagementComponent),
        canActivate: [rolesGuard(['admin', 'super_admin'])]
      },
      {
        path: 'invoices',
        loadComponent: () => import('./features/invoices/components/invoice-list/invoice-list.component').then(m => m.InvoiceListComponent),
        data: { filterType: 'all' }
      },
      {
        path: 'invoices/interstate',
        loadComponent: () => import('./features/invoices/components/invoice-list/invoice-list.component').then(m => m.InvoiceListComponent),
        data: { filterType: 'interstate' }
      },
      {
        path: 'invoices/intrastate',
        loadComponent: () => import('./features/invoices/components/invoice-list/invoice-list.component').then(m => m.InvoiceListComponent),
        data: { filterType: 'intrastate' }
      },
      {
        path: 'invoices/:id',
        loadComponent: () => import('./features/invoices/components/invoice-detail/invoice-detail.component').then(m => m.InvoiceDetailComponent)
      },
      {
        path: 'seller-statements',
        loadComponent: () => import('./features/seller-statements/components/seller-statement-list/seller-statement-list.component').then(m => m.SellerStatementListComponent),
        data: { filterType: 'all' }
      },
      {
        path: 'commission-bills',
        loadComponent: () => import('./features/seller-statements/components/seller-statement-list/seller-statement-list.component').then(m => m.SellerStatementListComponent),
        data: { filterType: 'commission-bill' }
      },
      {
        path: 'service-bills',
        loadComponent: () => import('./features/seller-statements/components/seller-statement-list/seller-statement-list.component').then(m => m.SellerStatementListComponent),
        data: { filterType: 'service-bill' }
      },
      {
        path: 'seller-statements/:id',
        loadComponent: () => import('./features/seller-statements/components/seller-statement-detail/seller-statement-detail.component').then(m => m.SellerStatementDetailComponent)
      },
      {
        path: 'trader-invoices',
        loadComponent: () => import('./features/trader-invoices/components/trader-invoice-list/trader-invoice-list.component').then(m => m.TraderInvoiceListComponent)
      },
      {
        path: 'trader-invoices/:id',
        loadComponent: () => import('./features/trader-invoices/components/trader-invoice-detail/trader-invoice-detail.component').then(m => m.TraderInvoiceDetailComponent)
      },
      {
        path: 'registrations',
        loadComponent: () => import('./features/registrations/registration-list.component').then(m => m.RegistrationListComponent)
      },
      {
        path: 'registrations/:id',
        loadComponent: () => import('./features/registrations/registration-detail.component').then(m => m.RegistrationDetailComponent)
      },
      {
        path: 'activities',
        loadComponent: () => import('./features/activities/components/activity-list/activity-list.component').then(m => m.ActivityListComponent)
      },
      {
        path: 'market-analysis',
        loadComponent: () => import('./market-analysis/market-analysis.component').then(m => m.MarketAnalysisComponent)
      }
    ]
  }
];
